/**
 * Privacy Policy Consent screen (Kickresume-style, Image 1)
 * Mandatory step after sign-up intent. Shown only in "after signup" flow.
 */

import { ArrowLeft } from 'lucide-react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../LoadingSpinner';

import { GGJLogoFull } from '../Brand/GGJLogo';

export interface PrivacyPolicyConsentScreenProps {
  onContinue: (consent: { terms: boolean; marketing?: boolean }) => void | Promise<void>;
  onBackToSignUp: () => void | Promise<void>;
  onNavigate?: (view: string) => void;
}

const PrivacyPolicyConsentScreen: React.FC<PrivacyPolicyConsentScreenProps> = ({
  onContinue,
  onBackToSignUp,
  onNavigate
}) => {
  const { t } = useTranslation();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingAccepted, setMarketingAccepted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!termsAccepted) return;
    setLoading(true);
    try {
      await onContinue({ terms: true, marketing: marketingAccepted });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    onBackToSignUp();
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] flex flex-col">
      {/* Header */}
      <header className="flex-shrink-0 border-b border-gray-800/50 bg-[#141824]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => onNavigate?.('home')}
            className="flex items-center hover:opacity-90 transition-opacity"
          >
            <GGJLogoFull iconSize={40} fontSize={20} textColor="#F2F2F3" />
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-400">{t('views.signupConsent.alreadyHaveAccount')}</span>
            <button
              type="button"
              onClick={() => onNavigate?.('login')}
              className="text-sm font-medium text-[#6C47FF] hover:text-[#5B8DFF] transition-colors min-h-[44px] min-w-[44px]"
            >
              {t('views.signupConsent.logIn')}
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4 sm:p-6 py-10 pb-[env(safe-area-inset-bottom)]">
        <div className="w-full max-w-xl">
          <h1 className="text-2xl md:text-3xl font-bold text-white text-center mb-6">
            {t('views.signupConsent.privacyPolicy')}
          </h1>

          <p className="text-gray-300 text-center text-sm md:text-base mb-6 leading-relaxed">
            {t('views.signupConsent.shortIntro')}
          </p>

          {/* Scrollable policy box */}
          <div className="mb-6 rounded-xl border border-gray-700/50 bg-[#141824] p-4 max-h-48 overflow-y-auto">
            <p className="text-gray-400 text-sm whitespace-pre-line leading-relaxed">
              {t('views.signupConsent.policyExcerpt')}
            </p>
          </div>

          {/* Checkboxes — 44px touch-friendly rows */}
          <div className="space-y-4 mb-8">
            <label className="flex items-start gap-3 cursor-pointer group min-h-[44px] py-2 -my-1">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1.5 w-5 h-5 rounded border-gray-600 bg-gray-800 text-[#6C47FF] focus:ring-[#6C47FF] focus:ring-offset-0 shrink-0 touch-manipulation"
              />
              <span className="text-sm text-gray-300 group-hover:text-white transition-colors">
                {t('views.signupConsent.agreePrefix')}{' '}
                <button type="button" onClick={() => window.open('/terms-of-service', '_blank')} className="text-[#6C47FF] hover:underline">{t('views.signupConsent.termsOfUse')}</button>
                ,{' '}
                <button type="button" onClick={() => window.open('/privacy-policy', '_blank')} className="text-[#6C47FF] hover:underline">{t('views.signupConsent.privacyPolicyLink')}</button>
                {' '}and{' '}
                <button type="button" onClick={() => window.open('/cookie-policy', '_blank')} className="text-[#6C47FF] hover:underline">{t('views.signupConsent.cookiePolicy')}</button>
                .
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer group min-h-[44px] py-2 -my-1">
              <input
                type="checkbox"
                checked={marketingAccepted}
                onChange={(e) => setMarketingAccepted(e.target.checked)}
                className="mt-1.5 w-5 h-5 rounded border-gray-600 bg-gray-800 text-[#6C47FF] focus:ring-[#6C47FF] focus:ring-offset-0 shrink-0 touch-manipulation"
              />
              <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                {t('views.signupConsent.marketingOptIn')}
              </span>
            </label>
          </div>

          {/* Continue button */}
          <button
            type="button"
            onClick={handleContinue}
            disabled={!termsAccepted || loading}
            className="w-full py-3 px-4 rounded-lg font-medium text-white disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 min-h-[44px] touch-manipulation"
            style={{
              background: termsAccepted
                ? 'var(--brand-cta-bg)'
                : 'rgb(55 65 81)'
            }}
          >
            {loading ? (
              <>
                <LoadingSpinner size="sm" showText={false} />
                <span>{t('views.signupConsent.continuing')}</span>
              </>
            ) : (
              t('views.signupConsent.continue')
            )}
          </button>

          {/* Back to Sign Up */}
          <button
            type="button"
            onClick={handleBack}
            className="mt-6 w-full min-h-[44px] flex items-center justify-center gap-2 text-sm text-gray-400 hover:text-white transition-colors touch-manipulation"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('views.signupConsent.backToSignUp')}
          </button>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicyConsentScreen;
